import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { AnswerContainer } from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/PromptHistory';
import WriteWithAISkeleton from '@buildwise/libs/posts/components/WriteWithAI/WriteWithAISkeleton';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import CheckFillIcon from 'remixicon-react/CheckFillIcon';
import ClipboardLineIcon from 'remixicon-react/ClipboardLineIcon';
import SummaryReferences from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/SummaryReferences';
import { formatNumberSequence } from '@buildwise/libs/helpers';

type ArticlesSummarizedResultsProps = {
  error: any;
  genAIResponse: any;
  isStreaming: any;
  loading: any;
  sourceRefsMetaData: any;
};

function ArticlesSummarizedResults({
  isStreaming,
  genAIResponse,
  loading,
  sourceRefsMetaData,
  error,
}: ArticlesSummarizedResultsProps) {
  const [openTooltip, setOpenTooltip] = useState(false);
  const answerContainerRef = useRef<HTMLDivElement | null>(null);
  const [metaDataOrder, setMetaDataOrder] = useState<
    { originalValue: number; orderValue: number }[]
  >([]);

  useEffect(() => {
    if (answerContainerRef.current) {
      answerContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      const allCitationElements = answerContainerRef.current.querySelectorAll('.citation');
      const formattedOrder = formatNumberSequence(
        Array.from(allCitationElements).map((element) => Number(element.id) || 0),
      );
      setMetaDataOrder?.(formattedOrder);
    }
  }, [answerContainerRef.current, genAIResponse]);

  const copyResponse = useCallback(async () => {
    try {
      if (answerContainerRef?.current?.innerHTML) {
        setOpenTooltip(true);
        const htmlContent = answerContainerRef.current.innerHTML;
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([answerContainerRef.current.innerText], { type: 'text/plain' }),
          }),
        ]);

        setTimeout(() => {
          setOpenTooltip(false);
        }, 1200);
      }
    } catch (error) {
      console.warn('COPY_RETROSPECTIVES_SUMMARY_ERROR', error);
    }
  }, [answerContainerRef.current]);

  return (
    <Box py={2} ref={answerContainerRef}>
      {(genAIResponse || loading || error) && (
        <>
          <AnswerContainer sx={{ width: '100%', borderRadius: '8px', mr: 0, p: 2 }}>
            {loading ? (
              <WriteWithAISkeleton />
            ) : isStreaming || genAIResponse ? (
              <>
                <RenderLLMMarkdown
                  llmResponse={genAIResponse || ''}
                  metaData={sourceRefsMetaData}
                  metaDataOrder={metaDataOrder}
                />
                {metaDataOrder.length > 0 &&
                  sourceRefsMetaData &&
                  sourceRefsMetaData?.length > 0 && (
                    <SummaryReferences
                      references={sourceRefsMetaData}
                      metaDataOrder={metaDataOrder}
                    />
                  )}
              </>
            ) : (
              !!error && (
                <Box
                  sx={{
                    height: '80%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 20 }}>{error}</Typography>
                </Box>
              )
            )}
          </AnswerContainer>
          {!isStreaming && !loading && !error && (
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <Tooltip open={openTooltip} placement="top" title={<Typography>Copied</Typography>}>
                <IconButton onClick={copyResponse}>
                  {openTooltip ? <CheckFillIcon size={18} /> : <ClipboardLineIcon size={18} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default ArticlesSummarizedResults;
